import { useMutation } from '@apollo/client'
import { ModalForm, PhoneInput, useForm, yup } from '@faceup/form'
import { Button, Checkbox, Flex, Modal, useMessage, useModal } from '@faceup/ui-base'
import { ChannelType, type Country } from '@faceup/utils'
import { useState } from 'react'
import { graphql } from '../__generated__'

const mutations = {
  approveAutomatedHotlineMutation: graphql(`
    mutation ApproveAutomatedHotlineMutation(
      $input: ApproveAutomatedHotlineInput!
    ) {
      approveAutomatedHotline(input: $input) {
        success
      }
    }
  `),
  approveLiveHotlineMutation: graphql(`
    mutation ApproveLiveHotlineMutation($input: ApproveLiveHotlineInput!) {
      approveLiveHotline(input: $input) {
        success
      }
    }
  `),
  approveAiHotlineMutation: graphql(`
    mutation ApproveAiHotlineMutation($input: ApproveAiHotlineInput!) {
      approveAiHotline(input: $input) {
        success
      }
    }
  `),
  deleteAutomatedHotlineMutation: graphql(`
    mutation DeleteAutomatedHotlineMutation($input: DeleteAutomatedHotlineInput!) {
      deleteAutomatedHotline(input: $input) {
        success
      }
    }
  `),
  deleteLiveHotlineMutation: graphql(`
    mutation DeleteLiveHotlineMutation($input: DeleteLiveHotlineInput!) {
      deleteLiveHotline(input: $input) {
        success
      }
    }
  `),
  deleteAiHotlineMutation: graphql(`
    mutation DeleteAiHotlineMutation($input: DeleteAiHotlineInput!) {
      deleteAiHotline(input: $input) {
        success
      }
    }
  `),
}

type ManageHotlineModalProps = {
  channelId: string
  type: ChannelType.AutomatedHotline | ChannelType.LiveHotline | ChannelType.AiHotline
  country: Country
  refetch: () => void
  name: string
  phoneNumber?: string | null
  isProcessing: boolean
}

export const ManageHotlineModal = ({
  channelId,
  type,
  refetch,
  name,
  country,
  phoneNumber,
  isProcessing,
}: ManageHotlineModalProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [keepNumber, setKeepNumber] = useState(false)
  const modal = useModal()
  const message = useMessage()

  const [approveAutomatedHotline] = useMutation(mutations.approveAutomatedHotlineMutation, {
    onCompleted: () => {
      setModalOpen(false)
      refetch()
    },
  })

  const [deleteAutomatedHotline, { loading: deletingAutomatedHotline }] = useMutation(
    mutations.deleteAutomatedHotlineMutation,
    {
      onCompleted: () => {
        message.success(`${name} deleted`)
        refetch()
      },
    }
  )

  const [approveAiHotline] = useMutation(mutations.approveAiHotlineMutation, {
    onCompleted: () => {
      setModalOpen(false)
      refetch()
    },
  })

  const [deleteAiHotline, { loading: deletingAiHotline }] = useMutation(
    mutations.deleteAiHotlineMutation,
    {
      onCompleted: () => {
        message.success(`${name} deleted`)
        refetch()
      },
    }
  )

  const [deleteLiveHotline, { loading: deletingLiveHotline }] = useMutation(
    mutations.deleteLiveHotlineMutation,
    {
      onCompleted: () => {
        message.success(`${name} deleted`)
        refetch()
      },
    }
  )

  const anyDeleting = deletingLiveHotline || deletingAutomatedHotline || deletingAiHotline

  const [approveLiveHotline] = useMutation(mutations.approveLiveHotlineMutation, {
    onCompleted: () => {
      setModalOpen(false)
      refetch()
    },
  })

  const handleDelete = () => {
    if (type === ChannelType.LiveHotline) {
      modal.confirm.danger({
        title: `Delete hotline ${name}`,
        confirmText: 'Delete',
        onConfirm: () => {
          deleteLiveHotline({ variables: { input: { channelId } } })
        },
      })
      return
    }

    setKeepNumber(false)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    setDeleteModalOpen(false)
    switch (type) {
      case ChannelType.AiHotline:
        deleteAiHotline({ variables: { input: { channelId, keepNumber } } })
        break
      case ChannelType.AutomatedHotline:
        deleteAutomatedHotline({ variables: { input: { channelId, keepNumber } } })
        break
    }
  }

  const schema = yup.object({
    phoneNumber: yup.string().required(),
  })

  const form = useForm({
    schema,
    afterSubmit: 'persistValues',
  })

  return (
    <>
      <Modal
        open={deleteModalOpen}
        title={`Delete hotline ${name}`}
        onClose={() => setDeleteModalOpen(false)}
        footer={
          <Flex gap={8} justify='flex-end'>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button
              danger
              type='primary'
              onClick={handleDeleteConfirm}
              loading={
                type === ChannelType.AiHotline
                  ? deletingAiHotline
                  : type === ChannelType.AutomatedHotline
                    ? deletingAutomatedHotline
                    : false
              }
            >
              Delete
            </Button>
          </Flex>
        }
      >
        {phoneNumber && (
          <Checkbox checked={keepNumber} onChange={e => setKeepNumber(e.target.checked)}>
            Keep Vonage phone number ({phoneNumber})
          </Checkbox>
        )}
      </Modal>
      <Flex gap={8}>
        {isProcessing && (
          <Button type='primary' onClick={() => setModalOpen(true)}>
            Approve
          </Button>
        )}
        <Button danger onClick={handleDelete} loading={anyDeleting}>
          Delete
        </Button>
      </Flex>
      <ModalForm
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title='Approve hotline'
        form={form}
        submitButtonText='save'
        onSubmit={async values => {
          const input = {
            phoneNumber: Number(values.phoneNumber),
            channelId,
          }

          switch (type) {
            case ChannelType.AiHotline:
              await approveAiHotline({ variables: { input } })
              return true
            case ChannelType.LiveHotline:
              await approveLiveHotline({ variables: { input } })
              return true
            case ChannelType.AutomatedHotline:
              await approveAutomatedHotline({
                variables: { input },
              })
              return true
          }
        }}
      >
        <PhoneInput
          label='Phone number'
          control={form.control}
          disableDropdown
          countryCodeEditable={false}
          name='phoneNumber'
          country={country}
        />
      </ModalForm>
    </>
  )
}
